// Shapes returned by /api/performance, /api/skills and /api/org-goals.
// Kept in one file so the list page, the detail page and the admin panels all
// agree on the contract rather than each declaring its own near-copy.

// Every review is ANNUAL; probation is a status on it (see ProbationInfo).
export type ReviewType = "ANNUAL";
export type ProbationOutcome = "CONFIRM" | "EXTEND" | "PIP" | "TERMINATE";
export type GoalStatus = "achieved" | "partially_achieved" | "not_achieved";
export type AssignmentStatus = "above_expectation" | "meet_expectation" | "not_meet_expectation";

export interface ReviewListRow {
  id: string;
  fiscalYear: number;
  fiscalYearLabel: string;
  reviewType: ReviewType;
  // True only on the "team" tab when the viewer sees this row as the active
  // temporary manager (not a direct report).
  isTemporary?: boolean;
  status: string;
  employee: {
    id: string;
    name: string;
    employeeCode: string;
    department: string | null;
    designation: string;
    location: string | null;
    country: string | null;
    skillCount: number;
    tenure: string;
  };
  manager: string | null;
  employeeComment: string | null;
  managerComment: string | null;
  managerFinalRating: number | null;
  hrAcknowledgementDate: string | null;
  // The employee is currently on probation (shown as an "On probation" tag).
  onProbation: boolean;
  probationEndDate: string | null;
  // The annual review that carries the employee's probation, if any.
  probationReviewId: string | null;
  probationOutcome: ProbationOutcome | null;
}

export interface ListMeta {
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

export interface EmployeeSkillRow {
  id: string;
  skillId: string;
  skillName: string;
  displayName: string;
  skillTypeId: string;
  skillTypeName: string;
  // Null until a manager sets the level during the review.
  expertiseLevelId: string | null;
  expertiseLevelName: string | null;
  weightPercent: number | null;
  belowExpert: boolean;
  agreedActionPlan: string | null;
  actionPlanReviewOn: string | null;
  actionPlanCompleted: boolean;
  knowledgeSharing: boolean | null;
  meetExpectation: boolean | null;
  comment: string | null;
}

export interface AssignedGoalRow {
  assignmentId: string;
  goalId: string;
  title: string;
  description: string | null;
  scope: string;
  asManager: boolean;
  status: AssignmentStatus | null;
  comment: string | null;
  assignedAt: string;
  assignedByName: string | null;
}

export interface PeriodGoalRow {
  id: string;
  title: string;
  description: string | null;
  startPeriodKey: string;
  extendsThroughPeriodKey: string;
  carriedForward: boolean;
  cumulativePercent: number | null;
  employeeStatus: GoalStatus | null;
  supervisorStatus: GoalStatus | null;
  employeeComments: string | null;
  supervisorComments: string | null;
}

export interface PeriodSide {
  utilization: number | null;
  productivity: number | null;
  qcPercent: number | null;
  rating: number | null;
  comments: string | null;
  submitted: boolean;
}

export interface ReviewPeriod {
  periodKey: string;
  seq: number;
  label: string;
  startDate: string;
  endDate: string;
  employee: PeriodSide;
  manager: PeriodSide & { actionPlan: string | null; goalsSubmitted: boolean };
  goals: PeriodGoalRow[];
}

// Mirrors performance.access.ts. The server sends this so the UI enables and
// disables controls from exactly the object the server enforces with.
export interface ReviewCapabilities {
  canView: boolean;
  canDelete: boolean;
  canEditSkills: boolean;
  canEditAssignedGoals: boolean;
  canEditAssignedGoalsSummary: boolean;
  canAddGoals: boolean;
  canEditFinalEmployee: boolean;
  canEditFinalManager: boolean;
  canUnlockFinalEmployee: boolean;
  canUnlockFinalManager: boolean;
  canHrAcknowledge: boolean;
  // Probation (a status on the anchor annual review).
  canViewProbation: boolean;
  canFillProbationForm: boolean;
  canReviewProbationForm: boolean;
  canReopenProbationForm: boolean;
  canDownloadProbationPdf: boolean;
  canDecideProbation: boolean;
  isEmployee: boolean;
  isLineManager: boolean;
  isHR: boolean;
  isSuperuser: boolean;
  hrLocked: boolean;
  // Temporary Manager Support (spec 10, temporary flow).
  isTemporaryManager: boolean;
  // Sees the review only as its temporary manager (not employee/line-manager/HR):
  // employee header + skills (read-only) + temp-manager panel only.
  isTemporaryOnlyViewer: boolean;
  canGrantTemporaryManager: boolean;
  canRevokeTemporaryManager: boolean;
  canEditTemporaryManagerComment: boolean;
  // The delegate may finalise (submit) their report; open only until submitted.
  canSubmitTemporaryManagerComment: boolean;
  // The active delegate has submitted their report (comment is final/locked).
  temporaryManagerReportSubmitted: boolean;
  canEditEmployeePeriod: Record<string, boolean>;
  canEditManagerPeriod: Record<string, boolean>;
  canEditPeriodGoals: Record<string, boolean>;
  canUnlockEmployeePeriod: Record<string, boolean>;
  canUnlockManagerPeriod: Record<string, boolean>;
  canUnlockPeriodGoals: Record<string, boolean>;
}

export interface ReviewDetail {
  id: string;
  reviewType: ReviewType;
  fiscalYear: number;
  fiscalYearLabel: string;
  status: string;
  qcApplies: boolean;
  header: {
    employeeId: string;
    employeeCode: string;
    fullName: string;
    email: string;
    department: string | null;
    designation: string;
    role: string;
    location: string | null;
    country: string | null;
    manager: string | null;
    hireDate: string;
    tenure: string;
    probationEndDate: string | null;
    status: string;
  };
  skills: EmployeeSkillRow[];
  assignedGoals: AssignedGoalRow[];
  assignedGoalsComment: string | null;
  periods: ReviewPeriod[];
  allPeriodKeys: string[];
  periodSeqByKey: Record<string, number>;
  yearEnd: {
    employeeComments: string | null;
    employeeSubmitted: boolean;
    mgrJobKnowledge: number | null;
    mgrWorkOutput: number | null;
    mgrOwnership: number | null;
    mgrQualityAdherence: number | null;
    mgrInitiative: number | null;
    mgrTeamPlayer: number | null;
    mgrContinuousLearning: number | null;
    mgrAttendanceDiscipline: number | null;
    mgrFinalRating: number | null;
    managerComments: string | null;
    managerSubmitted: boolean;
  };
  onProbation: boolean;
  probation: ProbationInfo | null;
  hr: { acknowledgementDate: string | null; comments: string | null; attachments: HrAttachment[] };
  temporaryManager: TemporaryManagerDelegation | null;
  // Every temporary-manager period this viewer may see, newest first.
  temporaryManagers: TemporaryManagerDelegation[];
  capabilities: ReviewCapabilities;
}

// An optional file HR attached next to the HR comments (HR-only).
export interface HrAttachment {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number | null;
  uploadedAt: string;
}

// Temporary Manager Support (spec 10, temporary flow only).
export interface TemporaryManagerDelegation {
  id: string;
  delegateId: string;
  delegateName: string;
  grantedById: string;
  grantedByName: string;
  startDate: string;
  endDate: string | null;
  comment: string | null;
  commentSubmittedAt: string | null;
  revokedAt: string | null;
  revokedByName: string | null;
  isActive: boolean;
  createdAt: string;
}

export interface PipRecord {
  id: string;
  employeeId: string;
  startDate: string;
  endDate: string;
  reason: string;
  goals: string;
  status: string;
  outcome: string | null;
  /** Set when HR closes the PIP (improved / exit). */
  decidedAt: string | null;
  /** End date before the latest extension; null if never extended. */
  previousEndDate: string | null;
  extensionCount: number;
  reviewId: string | null;
  employee: { id: string; firstName: string; lastName: string; employeeCode: string };
  initiatedBy: { id: string; firstName: string; lastName: string };
  _count: { checkins: number };
}

export interface PipCheckin {
  id: string;
  weekStartDate: string;
  progressRating: number | null;
  managerComments: string | null;
  employeeComments: string | null;
}

export interface SkillType {
  id: string;
  name: string;
  prefix: string | null;
  isActive: boolean;
  thresholds: { id: string; skillTypeId: string; role: string; expertWeightPercent: number }[];
  _count?: { skills: number };
}

export interface SkillRow {
  id: string;
  name: string;
  skillTypeId: string;
  isActive: boolean;
  /** Auto-assigned to everyone in scope, at the default expertise level. */
  isDefault: boolean;
  /** Empty = every department. */
  departmentIds: string[];
  /** Empty = every role. */
  roles: string[];
  skillType: { id: string; name: string; prefix: string | null };
  _count?: { employeeSkills: number };
}

// "Technical Skills-Autocad": the skill type's prefix and the skill name joined
// by a dash, or just the name when the type has no prefix. Mirrors the API's
// skillDisplayName, which builds the name shown on reviews.
export function skillDisplayName(skill: Pick<SkillRow, "name" | "skillType">): string {
  const p = skill.skillType.prefix?.trim();
  return p ? `${p}-${skill.name}` : skill.name;
}

export interface ExpertiseLevel {
  id: string;
  name: string;
  weightPercent: number;
  seq: number;
  isActive: boolean;
  /** The single level stamped on auto-assigned default skills. */
  isDefault: boolean;
  _count?: { employeeSkills: number };
}

export interface OrgGoal {
  id: string;
  fiscalYear: number;
  title: string;
  description: string | null;
  isActive: boolean;
  scope: GoalScope;
  ownerManagerId: string | null;
  ownerManager: { id: string; firstName: string; lastName: string } | null;
  createdByName: string | null;
  assignments: {
    id: string;
    employeeId: string;
    asManager: boolean;
    status: AssignmentStatus | null;
    comment: string | null;
    employee: { id: string; firstName: string; lastName: string; employeeCode: string };
  }[];
}

export const GOAL_STATUS_LABELS: Record<GoalStatus, string> = {
  achieved: "Achieved",
  partially_achieved: "Partially achieved",
  not_achieved: "Not achieved",
};

// Organization: company-wide, set by HR. Team: a manager's goal for several
// people on their team. Individual: a manager's goal for exactly one person.
export type GoalScope = "ORGANIZATION" | "TEAM" | "INDIVIDUAL";

export const GOAL_SCOPE_LABELS: Record<GoalScope, string> = {
  ORGANIZATION: "Organization",
  TEAM: "Team",
  INDIVIDUAL: "Individual",
};

export const ASSIGNMENT_STATUS_LABELS: Record<AssignmentStatus, string> = {
  above_expectation: "Above expectation",
  meet_expectation: "Meet expectation",
  not_meet_expectation: "Not meet expectation",
};

export interface ProbationFormQuestion {
  key: string;
  question: string;
  answer: string | null;
}

// The probation block of a review. On the anchor review (isThisReview) it
// carries everything; on a later year's review it only points to the anchor.
export type ProbationInfo =
  | {
      reviewId: string;
      fiscalYearLabel: string;
      isThisReview: false;
      open: true;
      periodStart: string | null;
      periodEnd: string | null;
    }
  | {
      reviewId: string;
      fiscalYearLabel: string;
      isThisReview: true;
      open: boolean;
      overdue: boolean;
      periodStart: string;
      periodEnd: string | null;
      extensionCount: number;
      previousEndDate: string | null;
      outcome: ProbationOutcome | null;
      outcomeAt: string | null;
      extendToDate: string | null;
      // HR-only; null for everyone else (they see the status, not HR's notes).
      notes: string | null;
      pipRecordId: string | null;
      form: {
        opensOn: string;
        isOpen: boolean;
        savedAt: string | null;
        // Signing submits: the employee signed at submittedAt, the manager at reviewedAt.
        submittedAt: string | null;
        reviewedAt: string | null;
        employeeSignedName: string | null;
        managerSignedName: string | null;
        hasPdf: boolean;
        questions: ProbationFormQuestion[];
      };
    };

// Past-tense labels for a recorded probation outcome.
export const PROBATION_OUTCOME_DONE_LABELS: Record<ProbationOutcome, string> = {
  CONFIRM: "Confirmed",
  EXTEND: "Extended",
  PIP: "PIP opened",
  TERMINATE: "Terminated",
};

// TERMINATE stays only so older decided probations still show their label;
// HR can no longer choose it (see PROBATION_DECISIONS).
export const PROBATION_OUTCOME_LABELS: Record<ProbationOutcome, string> = {
  CONFIRM: "Confirm employment",
  EXTEND: "Extend probation",
  PIP: "Open a PIP",
  TERMINATE: "Terminate",
};

// What HR can choose now.
export const PROBATION_DECISIONS = ["CONFIRM", "EXTEND", "PIP"] as const satisfies readonly ProbationOutcome[];
export type ProbationDecision = (typeof PROBATION_DECISIONS)[number];
